<template>
  <div class="pane-header">
    <span class="icon"><icon-colored-creative /></span>
    <span class="text">{{ title }}</span>
    <div
      v-if="permissions != null"
      v-show="selectedItemsCount > 0 && isSupplyOrganization"
      class="action-buttons float-right">
      <button
        v-show="showApproveButton"
        class="btn btn-sm btn-success"
        name="approveCreatives"
        :disabled="!permissions.canWriteCreativemediaownerapproval"
        @click="promptForCreativesApproval">
        {{ `${capitalize($t('actions.approve'))} (${selectedItemsCount})` }}
      </button>
      <slot name="approveForSpecificNetworksButton" />
      <button
        v-show="showApproveForSpecificInventoryButton"
        class="btn btn-sm btn-success"
        name="approveCreativesForSpecificInventory"
        :disabled="!permissions.canWriteCreativemediaownerapproval"
        @click="() => emits('approveForSpecificInventory')">
        {{ `${capitalize($t('actions.approveForSpecificInventory'))} (${selectedItemsCount})` }}
      </button>
      <button
        v-show="showRejectButton"
        class="btn btn-sm btn-danger"
        name="rejectCreatives"
        :disabled="!permissions.canWriteCreativemediaownerapproval"
        @click="promptForCreativesRejection">
        {{ `${capitalize($t('actions.reject'))} (${selectedItemsCount})` }}
      </button>
    </div>
  </div>
  <div class="row creatives-table-container">
    <div
      :id="tableId"
      :class="`col-12 ${tableId}`">
      <HSTable
        :id="tableId"
        ref="table"
        :allow-selection="isTableSelectable"
        :category="`PendingApprovals > ${title}`"
        :columns="columns"
        :fixed-filters="fixedFilters"
        :is-loading="isLoading"
        :is-editable="true"
        :items="creatives"
        :text-search-properties="textSearchProperties"
        :total-count="itemsTotalCount"
        :sort-column="sortColumn"
        :sort-ascending="false"
        :track-by="trackBy"
        data-row-height="4rem"
        enable-search-section
        pagination
        show-filters
        allow-row-expansion
        @selection-changed="selectionChanged"
        @filters-changed="refresh">
        <template #no-records>
          <no-records-table
            :icon="markRaw(IconComplexDone)"
            :header-text="$t('messages.tablePlaceholderNoPendingApprovalsForExternalCreativesTitle')"
            :details-text="$t('messages.tablePlaceholderNoPendingApprovalsForExternalCreativesDescription')" />
        </template>
        <template #no-match>
          <no-records-table
            :icon="markRaw(IconComplexCreative)"
            :header-text="$t('messages.tablePlaceholderNoMatchingCreativesTitle')"
            :details-text="$t('messages.tablePlaceholderNoMatchingItemsDescription')" />
        </template>
        <template
          v-for="(_, slot) in $slots"
          #[slot]="{ item }">
          <slot
            :name="slot"
            v-bind="{ creative: item as T }" />
        </template>
      </HSTable>
    </div>

    <!-- Approval modals -->
    <modal
      id="approve_creatives_confirmation_modal"
      :visible="isApproveCreativesModalVisible"
      :title="capitalize($t('actions.approveCreatives'))"
      :body="capitalize($t('messages.confirmApproveCreatives'))"
      :button1-caption="capitalize($t('actions.yes'))"
      :button2-caption="capitalize($t('actions.no'))"
      :button2-visible="true"
      @confirmed="() => legacyApprovalModalConfirmationResolver(true)"
      @cancelled="() => legacyApprovalModalConfirmationResolver(false)" />
    <reject-creative-modal
      id="reject_creatives_confirmation_modal"
      :visible="isRejectCreativesModalVisible"
      :is-for-single-creative="false"
      :is-from-deal-details-view="isFromDealDetailsView"
      :needs-comment="true"
      @confirmed="(comment) => rejectModalConfirmationResolver({ confirm: true, comment })"
      @cancelled="() => rejectModalConfirmationResolver({ confirm: false })" />
    <InventoryTargetingApprovalModal ref="inventoryTargetingModal" />
  </div>
</template>

<script setup lang="ts" generic="T extends Creative">
  import { capitalize, computed, markRaw, onBeforeMount, onMounted, ref, useTemplateRef } from 'vue';
  import { ComponentExposed } from 'vue-component-type-helpers';

  import Modal from '@/common/components/controls/Modal.vue';
  import NoRecordsTable from '@/common/components/controls/table/NoRecordsTable.vue';
  import HSTable from '@/common/components/controls/table/Table.vue';
  import TableColumn from '@/common/components/controls/table/TableColumn';
  import { IconColoredCreative } from '@/common/icons/colored';
  import { IconComplexCreative, IconComplexDone } from '@/common/icons/complex';
  import { Creative, FilterCollection, QueryParams } from '@/common/models';
  import InventoryTargetingApprovalModal from '@/org-app/components/creatives/approval/InventoryTargetingApprovalModal.vue';
  import RejectCreativeModal from '@/org-app/components/creatives/RejectCreativeModal.vue';
  import { useLanguages } from '@/org-app/services/tanstack/queries';
  import { waitForPendingQueries } from '@/org-app/services/tanstack/queryUtils';
  import { waitUntil } from '@/utils/DelayHelper';
  import { mapGetters, mapOrgAppGetters } from '@/utils/StateMapping';

  type FixedSlots = {
    approveForSpecificNetworksButton;
    expandedRow: (props: { creative: T }) => unknown;
  };

  type TableSlots<ColumnKeys extends string, T> = {
    [key in ColumnKeys]: (props: { creative: T }) => unknown;
  };

  type ColumnKeys = keyof IProps['columns'];

  // Provides proper typing for the slots
  defineSlots<FixedSlots & TableSlots<ColumnKeys, T>>();

  interface IProps {
    title: string;
    allowSelection: boolean;
    isFromDealDetailsView?: boolean;
    fixedFilters: FilterCollection[];
    tableId: string;
    columns: Record<string, TableColumn>;
    sortColumn: TableColumn;
    isLoading: boolean;
    creatives: T[];
    itemsTotalCount: number;
    showApproveButton: boolean;
    showRejectButton: boolean;
    showApproveForSpecificInventoryButton: boolean;
    selectedItemsCount: number;
    textSearchProperties: string[];
    trackBy?: string;
  }

  const { title, allowSelection, isFromDealDetailsView, tableId } = defineProps<IProps>();

  interface IEmits {
    (e: 'fetchItems', params: QueryParams): void;
    (e: 'selectionChanged', selectedCreatives: T[]): void;
    (e: 'approveSelectedItems'): void;
    (e: 'rejectSelectedItems', comment?: string): void;
    (e: 'approveForSpecificInventory'): void;
  }

  const emits = defineEmits<IEmits>();

  const { permissions } = mapGetters();
  const { isSupplyOrganization } = mapOrgAppGetters();

  const languagesQuery = useLanguages();

  // Legacy Approval Modal
  const isApproveCreativesModalVisible = ref(false);
  const legacyApprovalModalConfirmationResolver = ref<(confirm: boolean) => void>();

  // Reject Creative Modal
  interface IRejection {
    confirm: boolean;
    comment?: string;
  }
  const isRejectCreativesModalVisible = ref(false);
  const rejectModalConfirmationResolver = ref<(rejection: IRejection) => void>();

  // Approve Creatives For Specific Inventory Modal
  const inventoryTargetingModal =
    useTemplateRef<InstanceType<typeof InventoryTargetingApprovalModal>>('inventoryTargetingModal');

  const table = useTemplateRef<ComponentExposed<typeof HSTable>>('table');

  const isTableSelectable = computed(() => isSupplyOrganization && allowSelection);

  function selectionChanged(selectedCreatives: T[]) {
    emits('selectionChanged', selectedCreatives);
  }

  async function promptForCreativesApproval() {
    isApproveCreativesModalVisible.value = true;
    const promise = new Promise<boolean>((resolve) => {
      legacyApprovalModalConfirmationResolver.value = resolve;
    });
    const confirmation = await promise;
    if (confirmation) {
      emits('approveSelectedItems');
    }
    isApproveCreativesModalVisible.value = false;
    legacyApprovalModalConfirmationResolver.value = null;
  }

  async function promptForCreativesRejection() {
    // Prompt for rejection
    isRejectCreativesModalVisible.value = true;
    const promise = new Promise<IRejection>((resolve) => {
      rejectModalConfirmationResolver.value = resolve;
    });
    const rejection = await promise;
    if (rejection.confirm) {
      emits('rejectSelectedItems', rejection.comment);
    }
    isRejectCreativesModalVisible.value = false;
    rejectModalConfirmationResolver.value = null;
  }

  async function refresh() {
    await waitUntil(() => table.value != null);
    const { queryParams } = table.value!;
    emits('fetchItems', queryParams);
  }

  onBeforeMount(() => {
    waitForPendingQueries([languagesQuery]);
  });

  onMounted(() => {
    refresh();
  });

  defineExpose({
    refresh,
    inventoryTargetingModal,
  });
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;

  #app {
    .creatives-table-container {
      margin-bottom: 3rem;

      .actions-btn {
        padding: 0.3rem !important;
      }

      .status-container,
      .status-reject-message {
        display: flex;
        flex-direction: row;
        align-items: center;
      }
    }
  }

  .item-name-cell {
    display: flex;
    align-items: center;
    .selected-items-count-badge {
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: red;
      padding: 0.5rem;
      border-radius: 0.8rem;
      height: 1.8rem;
      width: 1.8rem;
      color: #fff;
      margin-right: 1rem;
      font-weight: 700;
      font-size: 1.1rem;
    }
  }
</style>
